import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import sentinelNetwork from '@/lib/api/sentinelNetwork';

interface ProjectNameContextType {
  projectName: string;
  appId: string;
  projectConfig: Record<string, any> | null;
  dataSources: any[];
  configLoading: boolean;
  setProject: (appId: string, name: string) => void;
  clearProject: () => void;
  loadProject: (appId: string) => Promise<{ config: Record<string, any>; dataSources: any[] }>;
  refreshProject: (appId: string) => Promise<void>;
}

const ProjectNameContext = createContext<ProjectNameContextType>({
  projectName: '',
  appId: '',
  projectConfig: null,
  dataSources: [],
  configLoading: false,
  setProject: () => {},
  clearProject: () => {},
  loadProject: async () => ({ config: {}, dataSources: [] }),
  refreshProject: async () => {},
});

export const useProjectName = () => useContext(ProjectNameContext);

export const ProjectNameProvider = ({ children }: { children: ReactNode }) => {
  const [projectName, setProjectName] = useState('');
  const [appId, setAppId] = useState('');
  const [projectConfig, setProjectConfig] = useState<Record<string, any> | null>(null);
  const [dataSources, setDataSources] = useState<any[]>([]);
  const [configLoading, setConfigLoading] = useState(false);
  const loadedAppIdRef = useRef<string>('');

  const setProject = (id: string, name: string) => {
    setAppId(id);
    setProjectName(name);
  };

  const clearProject = () => {
    setAppId('');
    setProjectName('');
    setProjectConfig(null);
    setDataSources([]);
    loadedAppIdRef.current = '';
  };

  const fetchProjectData = useCallback(async (id: string) => {
    setConfigLoading(true);
    try {
      const [configRes, dsRes] = await Promise.all([
        sentinelNetwork.get(`/config/project/${id}`),
        sentinelNetwork.get(`/config/project/${id}/data-sources`),
      ]);
      const config = configRes.data?.data || {};
      const ds: any[] = dsRes.data?.data || [];

      setProjectConfig(config);
      setDataSources(ds);
      setAppId(id);
      loadedAppIdRef.current = id;

      if (config.appName) {
        setProjectName(config.appName);
      }

      return { config, dataSources: ds };
    } catch (err) {
      console.error('Failed to load project data:', err);
      throw err;
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const loadProject = useCallback(async (id: string) => {
    if (loadedAppIdRef.current === id && projectConfig) {
      return { config: projectConfig, dataSources };
    }
    return fetchProjectData(id);
  }, [projectConfig, dataSources, fetchProjectData]);

  const refreshProject = useCallback(async (id: string) => {
    await fetchProjectData(id);
  }, [fetchProjectData]);

  return (
    <ProjectNameContext.Provider value={{
      projectName, appId, projectConfig, dataSources, configLoading,
      setProject, clearProject, loadProject, refreshProject,
    }}>
      {children}
    </ProjectNameContext.Provider>
  );
};
